/**
 * Sidebar Component
 * Collapsible vertical navigation for dashboard pages
 * - Expanded: icon + text
 * - Collapsed: icon only
 * - Settings has submenu (Account, API Keys)
 */

import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../redux";
import { logout } from "../../redux/slices/authSlice";
import { ROUTES } from "../../services/routes";
import {
  LayoutDashboard,
  Briefcase,
  MessageSquare,
  Settings,
  LogOut,
  Zap,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Bot,
  User,
  Key,
} from "lucide-react";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  subItems?: { path: string; label: string; icon: React.ReactNode }[];
}

export const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [settingsOpen, setSettingsOpen] = useState(
    location.pathname.startsWith("/settings")
  );

  const handleLogout = () => {
    dispatch(logout());
    navigate(ROUTES.HOME);
  };

  // Get 2-letter initials from first_name and last_name
  const getInitials = (): string => {
    const first = user?.first_name?.charAt(0)?.toUpperCase() || "";
    const last = user?.last_name?.charAt(0)?.toUpperCase() || "";
    if (first && last) return first + last;
    if (first) return first + (user?.first_name?.charAt(1)?.toUpperCase() || "");
    return "U";
  };

  // Get full name from first_name and last_name
  const getFullName = (): string => {
    const parts = [user?.first_name, user?.last_name].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : "User";
  };

  // Determine nav items based on role
  const adminNavItems: NavItem[] = [
    {
      path: ROUTES.ADMIN_DASHBOARD,
      label: "Overview",
      icon: <LayoutDashboard className="w-5 h-5" />,
    },
    {
      path: ROUTES.ADMIN_USERS,
      label: "Users",
      icon: <User className="w-5 h-5" />,
    },
    {
      path: ROUTES.ADMIN_JOBS,
      label: "Jobs",
      icon: <Briefcase className="w-5 h-5" />,
    },
    {
      path: ROUTES.ADMIN_CHATS,
      label: "Chats",
      icon: <MessageSquare className="w-5 h-5" />,
    },
  ];

  const mainNavItems: NavItem[] = [
    {
      path: ROUTES.DASHBOARD,
      label: "Dashboard",
      icon: <LayoutDashboard className="w-5 h-5" />,
    },
    {
      path: ROUTES.JOBS,
      label: "Jobs",
      icon: <Briefcase className="w-5 h-5" />,
    },
    {
      path: ROUTES.CHAT,
      label: "AI Chat",
      icon: <MessageSquare className="w-5 h-5" />,
    },
    {
      path: ROUTES.LIVE_DEMO,
      label: "Live Demo",
      icon: <Zap className="w-5 h-5" />,
    },
  ];

  const currentNavItems = user?.role === "admin" ? adminNavItems : mainNavItems;

  // Filter settings items based on role (Admins don't need API Keys)
  const settingsSubItems = [
    { path: "/settings/account", label: "Account", icon: <User className="w-4 h-4" /> },
    ...(user?.role !== "admin" 
      ? [{ path: "/settings/api-keys", label: "API Keys", icon: <Key className="w-4 h-4" /> }]
      : []
    ),
  ];

  const externalLink = {
    url: "https://intelli-vision-agent.vercel.app",
    label: "UniVision Agent",
    icon: <Bot className="w-5 h-5" />,
  };

  const isSettingsActive = location.pathname.startsWith("/settings");

  return (
    <aside
      className={`relative flex flex-col h-screen bg-slate-900/95 backdrop-blur-xl border-r border-slate-800/50 transition-all duration-300 z-50 ${
        collapsed ? "w-[72px]" : "w-[240px]"
      }`}
    >
      {/* Collapse Toggle - positioned on the edge */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-6 z-50 w-6 h-6 bg-slate-800 border border-slate-700 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors shadow-lg"
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {collapsed ? (
          <ChevronRight className="w-3.5 h-3.5" />
        ) : (
          <ChevronLeft className="w-3.5 h-3.5" />
        )}
      </button>

      {/* Logo */}
      <div className="flex items-center p-4 border-b border-slate-800/50">
        <NavLink to={user?.role === "admin" ? ROUTES.ADMIN_DASHBOARD : ROUTES.DASHBOARD} className="flex items-center gap-3">
          <img
            src="/logo.png"
            alt="UniVision"
            className="w-8 h-8 rounded-lg flex-shrink-0"
          />
          {!collapsed && (
            <span className="text-lg font-bold text-white whitespace-nowrap">
              Uni<span className="text-cyan-400">Vision</span>
            </span>
          )}
        </NavLink>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {currentNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === ROUTES.ADMIN_DASHBOARD || item.path === ROUTES.DASHBOARD}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                isActive
                  ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                  : "text-slate-400 hover:text-white hover:bg-slate-800/50"
              } ${collapsed ? "justify-center" : ""}`
            }
            title={collapsed ? item.label : undefined}
          >
            {item.icon}
            {!collapsed && (
              <span className="font-medium text-sm">{item.label}</span>
            )}
          </NavLink>
        ))}

        {/* Settings with Submenu */}
        <div>
          <button
            onClick={() => {
              if (collapsed) {
                navigate("/settings/account");
              } else {
                setSettingsOpen(!settingsOpen);
              }
            }}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all w-full ${
              isSettingsActive
                ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                : "text-slate-400 hover:text-white hover:bg-slate-800/50"
            } ${collapsed ? "justify-center" : ""}`}
            title={collapsed ? "Settings" : undefined}
          >
            <Settings className="w-5 h-5" />
            {!collapsed && (
              <>
                <span className="font-medium text-sm flex-1 text-left">Settings</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    settingsOpen ? "rotate-180" : ""
                  }`}
                />
              </>
            )}
          </button>

          {/* Submenu */}
          {!collapsed && settingsOpen && (
            <div className="mt-1 ml-4 space-y-1">
              {settingsSubItems.map((subItem) => (
                <NavLink
                  key={subItem.path}
                  to={subItem.path}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm ${
                      isActive
                        ? "text-cyan-400 bg-cyan-500/10"
                        : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                    }`
                  }
                >
                  {subItem.icon}
                  <span>{subItem.label}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        {user?.role !== "admin" && <div className="my-3 border-t border-slate-800/50" />}

        {/* External Link - Only for non-admin users */}
        {user?.role !== "admin" && (
          <a
            href={externalLink.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800/50 transition-all ${
              collapsed ? "justify-center" : ""
            }`}
            title={collapsed ? externalLink.label : undefined}
          >
            {externalLink.icon}
            {!collapsed && (
              <>
                <span className="font-medium text-sm flex-1">
                  {externalLink.label}
                </span>
                <ExternalLink className="w-3.5 h-3.5 text-slate-500" />
              </>
            )}
          </a>
        )}
      </nav>

      {/* User Section */}
      <div className="p-3 border-t border-slate-800/50">
        <div
          className={`flex items-center gap-3 p-2 rounded-xl bg-slate-800/30 ${
            collapsed ? "justify-center" : ""
          }`}
        >
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0 overflow-hidden">
            {user?.avatar_url ? (
              <img 
                src={user.avatar_url} 
                alt="Avatar" 
                className="w-full h-full object-cover"
              />
            ) : (
              getInitials()
            )}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm truncate">
                {getFullName()}
              </p>
              <p className="text-slate-500 text-xs truncate">
                {user?.email || ""}
              </p>
            </div>
          )}
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className={`flex items-center gap-3 w-full mt-2 px-3 py-2.5 rounded-xl text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all ${
            collapsed ? "justify-center" : ""
          }`}
          title={collapsed ? "Sign Out" : undefined}
        >
          <LogOut className="w-5 h-5" />
          {!collapsed && <span className="font-medium text-sm">Sign Out</span>}
        </button>
      </div>
    </aside>
  );
};
